import * as R from "ramda";
import { Action, ActionFunction1 } from "redux-actions";
import { call, put, select } from "redux-saga/effects";
import { AxiosInterceptedResult } from "ts/api/interceptors";
import { IRequestPreviewPayload } from "../../actions/course-author/course-author";
import { IDisplayInfoResponse, IResolveResponse, ResolvableType } from "../../api/ResolveApi/resolve-api.types";
import { api } from "../../dependencies";
import { IOrganization } from "../../reducers/course-author/types";
import { SagaAction } from "../../sagas/types";
import * as selectors from "../../selectors/selectors";
import { ICourseInstPreviewData } from "../../api/ResolveApi/resolve-api.types";

interface IRequestPreviewCourseFA {
  onSuccess: ActionFunction1<string, Action<string>>;
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
}

export const requestPreviewCourseInst = (fa: IRequestPreviewCourseFA) =>
  function* _requestPreviewCourseInst(action: Action<IRequestPreviewPayload>): SagaAction {
    const courseId = action.payload.courseId;
    const accountId: string = yield select(selectors.accountId);
    const [courseInstErr, courstInstResp]: AxiosInterceptedResult<string> = yield call(
      api.courseAuthor.getOrCreateCourseInstByCourseByAccount,
      courseId,
      accountId
    );
    if (courseInstErr || !courstInstResp) {
      yield put(
        fa.onFailure({
          message: "The course has not been published, select the Publish button in the left hand menu.",
        })
      );
      return;
    }

    // logic copied from fetchAndResolveAllCourseInstances
    const [courseInstanceError, courseInstanceResponse]: AxiosInterceptedResult<[
      ICourseInstPreviewData
    ]> = yield call(api.resolve.freshCourseInstance.post, accountId, [courstInstResp.data]);
    if (courseInstanceError || !courseInstanceResponse) {
      yield put(fa.onFailure({ message: "Course unavailable for preview." }));
      return;
    }
    const courseData = courseInstanceResponse.data.find(d => d.value.courseId === courseId);
    if (courseData === undefined || !courseData.value.isValid) {
      yield put(
        fa.onFailure({
          message:
            "The course does not contain any content, add content to the course, then select the Publish button in the left hand menu.",
        })
      );
      return;
    }

    yield put(fa.onSuccess(courstInstResp.data));
  };

interface IFetchAdministableOrgsFA {
  onSuccess: ActionFunction1<IOrganization[], Action<IOrganization[]>>;
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
}

export const fetchAdministableOrgs = (fa: IFetchAdministableOrgsFA) =>
  function* _fetchAdministableOrgs(action: Action<string[]>): SagaAction {
    const orgIds = action.payload;
    const resolveIds: Array<[ResolvableType, string]> = orgIds.map(orgId => [ResolvableType.Organization, orgId]);
    const accountId: string = yield select(selectors.accountId);
    const [fetchedOrgsErr, fetchedOrgsResp]: AxiosInterceptedResult<Array<
      IResolveResponse<IDisplayInfoResponse>
    >> = yield call(api.resolve.displayInfo.post, accountId, resolveIds);
    if (fetchedOrgsErr || !fetchedOrgsResp) {
      yield put(fa.onFailure({ message: "Failed to fetch organizations" }));
      return;
    }
    const mappedOrgs: IOrganization[] = R.zip(orgIds, fetchedOrgsResp.data).map(([uuid, data]) => ({
      defaultLocale: null,
      displayName: data.value.displayName || "",
      organizationId: uuid,
      ownerOrganizationId: data.value.owner,
      releaseLine: "",
      style: null,
    }));
    yield put(fa.onSuccess(mappedOrgs));
  };
